import { addDays, format } from 'date-fns'
import { DEFAULT_TIMEZONE } from '@/constants/geo'
import type { FormType } from '@/hooks/shared/use-app-form'
import { parseCalendarDay } from '@/shared/utils/france-time.utils'
import { zonedDatetimeLocalToISO } from '@/shared/utils/zoned-time.utils'

const PICKED_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * The END bound a vacation window posts: the last instant of the picked day in
 * Paris, so the window INCLUDES that day (#2414).
 *
 * Dispatch reads a window as `tstzrange(from, to)`, end exclusive. Posted as
 * the bare `yyyy-MM-dd` (UTC midnight, `bound()` in the api), the end day
 * itself was never blocked — « 07/10 → 08/10 » left the technician available on
 * the 8th — and a one-day « 07/10 → 07/10 » was an empty range the database
 * refuses (`check_unavailability_twins`, end strictly after start): a 500.
 *
 * Paris, not UTC: dispatch prints the reason's dates in `BUSINESS_TIMEZONE`
 * (`formatVacationReason`), so a UTC end of day would read as the NEXT day
 * there and block 00:00–02:00 of it. Paris end of day is also how V1-imported
 * rows end (`22:59:59.999Z`), and its leading digits are still the picked day,
 * which is what the card shows (`formatCalendarDay`).
 *
 * Only a freshly picked day is converted — the `DateField` writes a bare
 * `yyyy-MM-dd`. An instant hydrated from the api is the stored bound and is
 * passed back untouched, so editing a period's reason never moves its end.
 */
export function vacationEndBound(value: string): string {
  const day = PICKED_DAY.test(value) ? parseCalendarDay(value) : undefined
  if (!day) return value

  const nextMidnight = zonedDatetimeLocalToISO(
    `${format(addDays(day, 1), 'yyyy-MM-dd')}T00:00`,
    DEFAULT_TIMEZONE
  )
  if (!nextMidnight) return value

  return new Date(new Date(nextMidnight).getTime() - 1).toISOString()
}

export const VACATION_WINDOW_ORDER_MESSAGE =
  'La date de fin ne peut pas précéder la date de début'

/**
 * The order rule both vacation forms share: the end may be the start's own
 * day, never an earlier one.
 *
 * Compared as the instants that will be POSTED — the start as is, the end
 * through `vacationEndBound` — because that is what the api's
 * `assertWindowOrdered` compares (#2447). Comparing the displayed days let a
 * V1 start at `…T23:00Z` (Paris midnight of the NEXT day, shown as the UTC day)
 * accept an end re-picked on the day its card shows: that end posts as the
 * Paris end of the PREVIOUS day, and the api answered a 400. A freshly picked
 * one-day window is still ordered: UTC midnight against Paris end of day (#2414).
 */
export function isVacationWindowOrdered(window: {
  unavailableFrom: string
  unavailableTo: string
}): boolean {
  if (!window.unavailableFrom || !window.unavailableTo) return true

  const start = new Date(window.unavailableFrom).getTime()
  const end = new Date(vacationEndBound(window.unavailableTo)).getTime()
  // Not a day this rule can read; the api refuses it in its own words.
  if (Number.isNaN(start) || Number.isNaN(end)) return true

  return end > start
}

/**
 * Wires the order rule onto a period's « Date de fin » field (#2447).
 *
 * The schemas' object-level refine cannot do it: the form reports its issue
 * under `vacationPeriods[0].unavailableTo` while the field is registered as
 * `vacationPeriods.0.unavailableTo`, and `ValidatingField` validates the end
 * against its own `z.string()` leaf — so the refine silently blocked the
 * submit and no field ever showed why. Here the end validates itself, again
 * whenever the start moves, and moving the start touches the end so the
 * error shows without waiting for the operator to open a field they never
 * meant to change.
 */
export function vacationWindowFields(form: FormType, index: number) {
  const startName = `vacationPeriods.${index}.unavailableFrom`
  const endName = `vacationPeriods.${index}.unavailableTo`

  return {
    startListeners: {
      onChange: () =>
        form.setFieldMeta(endName, (meta) => ({ ...meta, isTouched: true })),
    },
    endValidators: {
      onChangeListenTo: [startName],
      onChange: ({ value }: { value: unknown }) => {
        const start: unknown = form.getFieldValue(startName)

        return isVacationWindowOrdered({
          unavailableFrom: typeof start === 'string' ? start : '',
          unavailableTo: typeof value === 'string' ? value : '',
        })
          ? undefined
          : VACATION_WINDOW_ORDER_MESSAGE
      },
    },
  }
}

/**
 * Re-runs the order rule on every end left after a period is removed.
 *
 * Field meta is keyed by index and the forms remove a period by filtering the
 * array, so a removed period's error would stay on whichever period slides
 * into its index — and, `canSubmit` false, eat the next « Sauvegarder ».
 * `removeFieldValue` does not help: it shifts `vacationPeriods[0]` keys, and
 * these fields are registered as `vacationPeriods.0`.
 */
export function revalidateVacationEnds(form: FormType, count: number) {
  for (let index = 0; index < count; index++) {
    void form.validateField(`vacationPeriods.${index}.unavailableTo`, 'change')
  }
}
